import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../api';

const TYPE_ICONS = {
  ticket_update:   '🎫',
  ticket_assigned: '📋',
  ticket_comment:  '💬',
  sa_update:       '💰',
};

const POLL_INTERVAL = 60000; // poll every 60 seconds

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/api/notifications');
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch (e) { /* silent fail */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    await api.put('/api/notifications/read-all').catch(() => {});
    setNotifications(n => n.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) {
      await api.put(`/api/notifications/${notif.id}/read`).catch(() => {});
      setNotifications(n => n.map(x => x.id === notif.id ? { ...x, is_read: true } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (notif.ticket_id) navigate(`/tickets/${notif.ticket_id}`);
    else if (notif.sa_request_id) navigate(`/sa/${notif.sa_request_id}`);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', background: 'none', border: 'none',
          cursor: 'pointer', padding: '6px 8px', borderRadius: 8,
          fontSize: 18, lineHeight: 1,
          background: open ? 'var(--surface)' : 'transparent',
          transition: 'background 0.15s',
        }}
        title="Notifications"
      >
        🔔
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#D63A3A', color: '#fff',
            fontSize: 9, fontWeight: 700, fontFamily: 'Sora',
            borderRadius: 10, minWidth: 16, height: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', lineHeight: 1,
          }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 340, maxHeight: 440, overflowY: 'auto',
          background: 'var(--white)', border: '1px solid var(--border)',
          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 2000,
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, background: 'var(--white)', zIndex: 1,
          }}>
            <span style={{ fontFamily: 'Sora', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              Notifications {unread > 0 && <span style={{ color: '#D63A3A' }}>({unread})</span>}
            </span>
            {unread > 0 && (
              <button onClick={markAllRead} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: 'var(--sky)', fontWeight: 600,
              }}>
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          {notifications.length === 0 ? (
            <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
              No notifications yet
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.id}
                onClick={() => handleClick(n)}
                style={{
                  padding: '11px 16px',
                  borderBottom: '1px solid var(--border)',
                  cursor: n.ticket_id || n.sa_request_id ? 'pointer' : 'default',
                  background: n.is_read ? 'transparent' : 'var(--sky-light, #eff6ff)',
                  transition: 'background 0.1s',
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}
                onMouseEnter={e => { if (n.ticket_id || n.sa_request_id) e.currentTarget.style.background = 'var(--surface)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : 'var(--sky-light, #eff6ff)'; }}
              >
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                  {TYPE_ICONS[n.type] || '🔔'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12, color: 'var(--text)', fontWeight: n.is_read ? 400 : 600,
                    lineHeight: 1.4, wordBreak: 'break-word',
                  }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
                    {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                  </div>
                </div>
                {!n.is_read && (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#1B8FD4', flexShrink: 0, marginTop: 5,
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
